import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Copy, KeyRound, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

const copy = async (value) => {
  try { await navigator.clipboard.writeText(value); toast.success("Copiado"); }
  catch { toast.error("Não foi possível copiar. Selecione o texto e copie manualmente."); }
};

const CopyField = ({ label, value, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <div className="flex gap-2 mt-1">
      <Input readOnly value={value || ""} onFocus={(e) => e.target.select()} className="h-9 text-xs font-mono" data-testid={testid} />
      <Button type="button" size="sm" variant="outline" onClick={() => copy(value)}
              data-testid={`${testid}-copy`}><Copy className="w-3.5 h-3.5" /></Button>
    </div>
  </div>
);

export const DriveCredentials = ({ cfg, onSaved, connected }) => {
  const [clientId, setClientId] = useState(cfg.client_id || "");
  const [secret, setSecret] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const changing = clientId.trim() !== (cfg.client_id || "") || secret;
    if (connected && changing && !window.confirm("Trocar as credenciais desconecta o Drive atual. Será preciso conectar novamente. Continuar?")) return;
    setBusy(true);
    try {
      const { data } = await api.put("/drive/config", { client_id: clientId.trim(), ...(secret ? { client_secret: secret.trim() } : {}) });
      setSecret("");
      toast.success("Credenciais salvas");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao salvar credenciais"); }
    finally { setBusy(false); }
  };

  const clear = async () => {
    if (!window.confirm("Apagar as credenciais do Google? O Drive será desconectado.")) return;
    try {
      const { data } = await api.put("/drive/config", { clear_credentials: true });
      toast.success("Credenciais removidas");
      onSaved(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Falha ao remover"); }
  };

  return (
    <div className="space-y-3" data-testid="drive-credentials">
      <div className="text-sm font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4 text-emerald-600" /> Credenciais do Google Cloud</div>
      <div className="grid sm:grid-cols-2 gap-3">
        <CopyField label="Origem JavaScript autorizada" value={cfg.origin} testid="drive-origin" />
        <CopyField label="URI de redirecionamento autorizado" value={cfg.redirect_uri} testid="drive-redirect-uri" />
      </div>
      <div>
        <Label className="text-xs">Client ID</Label>
        <Input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="000000000000-xxxx.apps.googleusercontent.com"
               className="h-9 mt-1 font-mono text-xs" data-testid="drive-client-id-input" />
      </div>
      <div>
        <Label className="text-xs">Client Secret {cfg.has_secret && <span className="text-muted-foreground">(salvo: {cfg.secret_hint} — deixe em branco para manter)</span>}</Label>
        <div className="flex gap-2 mt-1">
          <Input type={show ? "text" : "password"} value={secret} onChange={(e) => setSecret(e.target.value)} autoComplete="off"
                 placeholder={cfg.has_secret ? "••••••••" : "GOCSPX-..."} className="h-9 font-mono text-xs" data-testid="drive-client-secret-input" />
          <Button type="button" size="sm" variant="outline" onClick={() => setShow((s) => !s)} data-testid="drive-secret-toggle">
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" onClick={save} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="drive-credentials-save">Salvar credenciais</Button>
        {(cfg.client_id || cfg.has_secret) && (
          <Button size="sm" variant="ghost" onClick={clear} className="text-destructive" data-testid="drive-credentials-clear">Remover credenciais</Button>
        )}
      </div>
    </div>
  );
};
